import { capitalize } from 'lodash';
import moment from 'moment';

import { Rate } from '../models/Rate';
import { Terminal } from '../models/Terminal';

export const getProvinceFromAddress = (address: string | undefined): string | null => {
  if (!address?.trim()) return null;
  const segs = address.split(',');
  if (segs.length > 1) {
    return segs[segs.length - 1].trim();
  }
  return null;
};

export const getAlphabet = (index: number) => {
  return String.fromCharCode('A'.charCodeAt(0) + index);
};

export const getCapitalizedSentence = (sentence?: string) => {
  if (!sentence) {
    return '';
  }
  return sentence
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
};

export const getInAddressTerminals = (
  terminals?: Terminal[],
  address?: string
) => {
  return (
    terminals?.filter(
      (terminal) =>
        `${terminal.city}, ${terminal.state}`.toLowerCase() ===
        address?.toLowerCase()
    ) || []
  );
};

export const toTerminalIds = (
  terminals?: Array<number | { id: number } | null | undefined> | null
): number[] => {
  if (!Array.isArray(terminals) || terminals.length === 0) return [];
  return Array.from(
    new Set(
      terminals
        .map((t) => (typeof t === 'object' && t !== null ? t.id : t))
        .filter((id) => id != null)
        .map(Number)
    )
  ).sort((a, b) => a - b);
};

export const terminalSetsOverlapOrBothEmpty = (
  a?: Array<number | { id: number } | null | undefined> | null,
  b?: Array<number | { id: number } | null | undefined> | null
): boolean => {
  const aIds = toTerminalIds(a);
  const bIds = toTerminalIds(b);
  if (aIds.length === 0 && bIds.length === 0) return true;
  if (aIds.length === 0 || bIds.length === 0) return false;
  const bSet = new Set(bIds);
  return aIds.some((id) => bSet.has(id));
};

export const formatCurrency = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  return `$${Number(value).toLocaleString('en-CA', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`;
};

export const timeAgo = (date: Date | string) => {
  return moment(date).fromNow();
};

export const formatDateTime = (date: Date) => {
  return date.toLocaleString('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hour12: true,
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const getTimeZone = () => {
  const timezoneOffset = new Date().getTimezoneOffset();
  const offset = Math.abs(timezoneOffset);
  const offsetOperator = timezoneOffset < 0 ? '+' : '-';
  const offsetHours = Math.floor(offset / 60)
    .toString()
    .padStart(2, '0');
  const offsetMinutes = Math.floor(offset % 60)
    .toString()
    .padStart(2, '0');

  return `UTC${offsetOperator}${offsetHours}:${offsetMinutes}`;
};

export const getfuelExpiryString = (fuelSurchargeExpiry: string) => {
  const currentDate = new Date();
  const targetTimeZone = getTimeZone();
  const fuelSurchargeExpiryDate = new Date(
    `${fuelSurchargeExpiry} ${targetTimeZone}`
  );
  const timeDifference =
    fuelSurchargeExpiryDate.valueOf() - currentDate.valueOf();
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  // Determine the color style based on the number of days remaining
  const color =
    daysDifference < 2 ? 'red' : daysDifference < 10 ? 'orange' : undefined; // No color if not less than 10 days
  return {
    color,
    date: fuelSurchargeExpiry.replaceAll('/', '-'),
  };
};

export const validateRateClasses = (
  rate: Rate,
  classIdsToValidate: number[],
  updateMutation: {
    mutate: (
      rate: Rate,
      options?: {
        onError?: (error: Error) => void;
        onSuccess?: (data: Rate) => void;
      }
    ) => void;
  }
): Promise<Rate> => {
  return new Promise((resolve, reject) => {
    if (classIdsToValidate.length === 0) {
      resolve(rate);
      return;
    }

    // update rate with validatedAt timestamp for selected rate classes
    const updatedRate = {
      ...rate,
      classRate: { ...rate.classRate },
    };

    const currentDate = new Date();

    classIdsToValidate.forEach((classId) => {
      if (
        updatedRate.classRate[classId] &&
        updatedRate.classRate[classId].value !== undefined
      ) {
        updatedRate.classRate[classId] = {
          name: updatedRate.classRate[classId].name,
          validatedAt: currentDate,
          value: updatedRate.classRate[classId].value,
        };
      }
    });

    updateMutation.mutate(updatedRate, {
      onError: reject,
      onSuccess: (data) => {
        resolve(data);
      },
    });
  });
};
